// Глобальный стор оболочки agentbox (zustand).
//
// Ключевое разделение понятий:
//   «сессия» — процесс на сервере (SessionInfo), живёт независимо от браузера;
//   «окно» (панель) — клиентское представление сессии в сетке, это просто id в paneIds.
// Закрытие окна убирает id из paneIds и НЕ трогает сессию на сервере (FR-7).
//
// Панели (panes/*) имеют право дёргать отсюда: setRtt(), upsertSession(), refreshSessions().

import { create } from 'zustand'
import { ApiError, api, getToken, notesApi, setToken, trackerApi } from '../api'
import type { ProfileInfo, SessionInfo, TreeNode } from '../protocol'
import { load, saveIfChanged } from '../lib/persist'
import { applyUpdate, isIos, isStandalone, promptInstall } from '../lib/pwa'
import { MOBILE_QUERY } from '../lib/useMediaQuery'

export type Theme = 'dark' | 'light'
/** Раздел оболочки UI v3 (README §1.1): семь рабочих, служебные «Кабинет»
 *  и «База знаний», плюс черновики разделов `draft:<id>` (README §10).
 *  Реестр — web/src/sections/registry.ts. Старое «base» читается как «notes». */
export type MainSection =
  | 'chats' | 'orchestrator' | 'feed' | 'flow' | 'tracker' | 'notes' | 'messengers' | 'library'
export type ServiceSection = 'account' | 'kb'
export type DraftSection = `draft:${string}`
/** Опубликованный пользовательский раздел (README §10, зона pages): `custom:<id>`. */
export type CustomSection = `custom:${string}`
export type Section = MainSection | ServiceSection | DraftSection | CustomSection

/** Выбор из Библиотек для карточки Конструктора (§8). Кладёт зона canvas. */
export interface LibraryPick { scheme: string; node: string; title: string }
/** Конструктор в режиме «расход» с этим агентом (§9 «кто тратит сейчас»). */
export interface FlowFocus { mode: 'cost'; agentId: string }
export const MAIN_SECTIONS: MainSection[] = [
  'chats', 'orchestrator', 'feed', 'flow', 'tracker', 'notes', 'messengers', 'library',
]

/** Вкладка черновика в верхней полосе («новый раздел · черновик», §10.1). */
export interface DraftTab {
  /** id раздела-черновика: `draft:<n>` */
  id: DraftSection
  title: string
  /** беседа, в которой агент собирает раздел */
  sessionId: string | null
}

/** Ширина бокового меню (§1.2): 248 по умолчанию, 200–420 или полоса 56. */
export const SIDE_DEFAULT = 248
export const SIDE_RAIL = 56
export const SIDE_W_MIN = 200
export const SIDE_W_MAX = 420
/** Левее этого при перетаскивании меню защёлкивается в полосу. */
export const SIDE_SNAP = 150
const SIDE_KEY = 'abx-side-w'
const PROTO_KEY = 'abx-proto-v2'

export function clampSideW(raw: number): number {
  if (!Number.isFinite(raw)) return SIDE_DEFAULT
  if (raw < SIDE_SNAP) return SIDE_RAIL
  return Math.min(SIDE_W_MAX, Math.max(SIDE_W_MIN, Math.round(raw)))
}

function readSideW(): number {
  try {
    const w = Number(localStorage.getItem(SIDE_KEY))
    if (w === SIDE_RAIL || (w >= SIDE_W_MIN && w <= SIDE_W_MAX)) return w
  } catch {
    // приватный режим — берём по умолчанию
  }
  return SIDE_DEFAULT
}

function writeSideW(w: number | null): void {
  try {
    if (w === null) localStorage.removeItem(SIDE_KEY)
    else localStorage.setItem(SIDE_KEY, String(w))
  } catch {
    // не критично
  }
}

interface Proto { screen?: string; theme?: Theme; themeTouched?: boolean }
function readProto(): Proto {
  try {
    const raw = JSON.parse(localStorage.getItem(PROTO_KEY) || '{}') as unknown
    return raw && typeof raw === 'object' ? (raw as Proto) : {}
  } catch {
    return {}
  }
}
const savedProto = readProto()
/** Экран внутри базы. «Трекер»/«Задачи» — PLAN.md «Трекер сообщений из Telegram». */
export type BaseView = 'document' | 'graph' | 'feed' | 'search' | 'tracker' | 'kanban'
export type Cols = 1 | 2 | 3
export type Rows = 1 | 2
/** Состояние связи для pill в шапке. RTT кладут сюда панели по своим ping/pong. */
export type LinkState = 'stable' | 'slow' | 'reconnecting' | 'offline'

export interface FontPair {
  key: string
  label: string
  sans: string
  mono: string
}

/** 9 пар шрифтов из design/README.md, раздел «Типографика». Первая — по умолчанию. */
export const FONT_PAIRS: FontPair[] = [
  { key: 'golos', label: 'Golos Text', sans: "'Golos Text', Helvetica, sans-serif", mono: "'IBM Plex Mono', ui-monospace, monospace" },
  { key: 'onest', label: 'Onest', sans: "Onest, Helvetica, sans-serif", mono: "'JetBrains Mono', ui-monospace, monospace" },
  { key: 'geist', label: 'Geist', sans: "Geist, Helvetica, sans-serif", mono: "'Geist Mono', ui-monospace, monospace" },
  { key: 'commissioner', label: 'Commissioner', sans: "Commissioner, Helvetica, sans-serif", mono: "'Martian Mono', ui-monospace, monospace" },
  { key: 'plex', label: 'Plex Sans', sans: "'IBM Plex Sans', Helvetica, sans-serif", mono: "'IBM Plex Mono', ui-monospace, monospace" },
  { key: 'grotesk', label: 'Space Grotesk', sans: "'Space Grotesk', Helvetica, sans-serif", mono: "'IBM Plex Mono', ui-monospace, monospace" },
  { key: 'manrope', label: 'Manrope', sans: "Manrope, Helvetica, sans-serif", mono: "'JetBrains Mono', ui-monospace, monospace" },
  { key: 'helvetica', label: 'Helvetica', sans: 'Helvetica, Arial, sans-serif', mono: "ui-monospace, 'SF Mono', Menlo, monospace" },
  { key: 'allmono', label: 'всё моно', sans: "'JetBrains Mono', ui-monospace, monospace", mono: "'JetBrains Mono', ui-monospace, monospace" },
]

// ---------- пропорции сплиттеров ----------

const MIN_RATIO = 0.18
const MAX_RATIO = 0.82

/** Доля первой колонки/строки → fr-коэффициент для grid-template. */
export function ratioToFr(ratio: number): number {
  const clamped = Math.min(MAX_RATIO, Math.max(MIN_RATIO, ratio))
  return clamped / (1 - clamped)
}
/** Обратное преобразование: fr → доля, чтобы позиционировать линию сплиттера. */
export function frToRatio(fr: number): number {
  return fr / (fr + 1)
}

// ---------- восстановление из localStorage ----------

interface SavedLayout { cols: number; rows: number; colFr: number; rowFr: number }
interface SavedPanes { ids: string[]; active: string | null }
/** Левое меню: ширина и раскрытые секции. Открытость — отдельным ключом «menu»:
 *  на телефоне меню всегда шторка, и её открытость запоминать незачем. */
interface SavedSide { width: number; chats: boolean; base: boolean; msg: boolean }
interface SavedNotes { view: BaseView; path: string | null; expanded: string[] }

// Тема по умолчанию тёмная (§1.3). Выбор пользователя — только с флагом
// themeTouched. Старый ключ agentbox.theme — это тоже выбор пользователя.
const legacyTheme = load<Theme | null>('theme', null)
const savedThemeTouched = savedProto.themeTouched === true || legacyTheme !== null
const savedTheme: Theme = savedProto.themeTouched
  ? (savedProto.theme === 'light' ? 'light' : 'dark')
  : legacyTheme === 'light' ? 'light' : 'dark'
const savedFont = load<number>('font', 0)
const savedLayout = load<SavedLayout>('layout', { cols: 2, rows: 1, colFr: 1, rowFr: 1 })
const savedPanes = load<SavedPanes>('panes', { ids: [], active: null })
/** Профиль прошлого сеанса: под другим профилем окна чужие, их надо забыть. */
const savedProfile = load<string | null>('profile', null)
const savedSection: unknown = savedProto.screen ?? load<string>('section', 'chats')
const savedSide = load<SavedSide>('sidebar', { width: 312, chats: true, base: true, msg: true })
const savedMenu = load<boolean>('menu', true)
const savedNotes = load<SavedNotes>('notes', { view: 'document', path: null, expanded: [] })
const savedSerif = load<boolean>('serif', true)

/** Пределы ширины левого меню (SPEC: 312 по умолчанию, тянется 200–460). */
export const SIDE_MIN = 200
export const SIDE_MAX = 460

/** Телефон определяем тем же запросом, что и раскладка: стор живёт вне React,
 *  а решения «закрывать ли меню» и «помнить ли его открытость» зависят от него. */
export function isMobileLayout(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches
}

const asCols = (n: number): Cols => (n === 1 || n === 2 || n === 3 ? n : 2)
const asRows = (n: number): Rows => (n === 2 ? 2 : 1)
const asFr = (n: number): number =>
  Number.isFinite(n) ? ratioToFr(frToRatio(n)) : 1
const asWidth = (n: number): number =>
  Number.isFinite(n) ? Math.min(SIDE_MAX, Math.max(SIDE_MIN, Math.round(n))) : 312
export const asSection = (value: unknown): Section => {
  if (value === 'base') return 'notes'
  if (value === 'msg') return 'messengers'
  if (value === 'market') return 'library'
  if (value === 'profile') return 'account'
  if (typeof value !== 'string') return 'chats'
  if ((MAIN_SECTIONS as string[]).includes(value)) return value as Section
  if (value === 'account' || value === 'kb') return value
  if (/^custom:[a-z][a-z0-9-]{1,39}$/.test(value)) return value as Section
  // черновики живут до перезагрузки — после неё вкладки уже нет
  return 'chats'
}
const asView = (value: unknown): BaseView =>
  value === 'feed' || value === 'search' || value === 'graph'
    || value === 'tracker' || value === 'kanban' ? value : 'document'

// ---------- применение темы и шрифта к <html> ----------

/** Цвет системной панели в установленном приложении — на каждую тему свой. */
const THEME_COLOR: Record<Theme, string> = { dark: 'black', light: 'black' }

function applyTheme(theme: Theme): void {
  const root = document.documentElement
  // Корень .abx с data-theme (§1.3) — на <html>, чтобы порталы тоже видели токены.
  root.classList.add('abx')
  root.dataset.theme = theme
  const meta = document.querySelector('meta[name="theme-color"]')
  if (!meta) return
  // Системная полоса PWA продолжает верхнюю полосу — она от темы не зависит.
  const bar = getComputedStyle(root).getPropertyValue('--topbar-bg').trim()
  meta.setAttribute('content', bar || THEME_COLOR[theme])
}

function applyFont(index: number): void {
  const pair = FONT_PAIRS[index] ?? FONT_PAIRS[0]
  document.documentElement.style.setProperty('--font-sans', pair.sans)
  document.documentElement.style.setProperty('--font-mono', pair.mono)
}

/** Шрифт чтения документа: «Aa» в шапке переключает сериф ↔ санс.
 *  Значение отдаём переменной --font-read, а не классом: вид «документ» пишет
 *  другая ветка, и договориться о переменной дешевле, чем о разметке. */
const READ_SERIF = "'Literata', Georgia, 'Times New Roman', serif"
function applyRead(serif: boolean): void {
  document.documentElement.dataset.read = serif ? 'serif' : 'sans'
  document.documentElement.style.setProperty(
    '--font-read',
    serif ? READ_SERIF : 'var(--font-sans)',
  )
}

// ---------- дерево базы знаний ----------

/** Сколько всего заметок в дереве — для счётчика у секции «БАЗА ДАННЫХ». */
export function countNotes(nodes: TreeNode[]): number {
  return nodes.reduce(
    (sum, node) => sum + (node.kind === 'note' ? 1 : countNotes(node.children)),
    0,
  )
}

/** Каталоги на пути к заметке: открывая заметку, раскрываем всё над ней. */
function ancestors(path: string): string[] {
  const parts = path.split('/')
  parts.pop()
  return parts.map((_, index) => parts.slice(0, index + 1).join('/'))
}

// ---------- человеческие тексты ошибок ----------

function detailOf(message: string): string {
  const text = message.trim()
  if (!text.startsWith('{')) return text
  try {
    const parsed = JSON.parse(text) as { detail?: unknown; message?: unknown }
    const value = parsed.detail ?? parsed.message
    return typeof value === 'string' ? value : text
  } catch {
    return text
  }
}

const TELEGRAM_DENIED = 'Телеграм подключён к профилю владельца сервера — у этого профиля канала нет'

export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 408) return 'Сервер не ответил вовремя — проверьте связь и попробуйте снова.'
    if (error.status === 429) return 'Слишком много попыток, подождите немного и попробуйте снова.'
    if (error.status === 401 || error.status === 403) return 'Неверный пароль доступа.'
    if (error.status === 404) return 'Сервер не знает такого адреса — проверьте версию agentbox.'
    if (error.status >= 500) return 'Сервер не отвечает: проверьте, что agentbox запущен, и загляните в его логи.'
    return detailOf(error.message) || `Ошибка ${error.status}`
  }
  if (error instanceof Error && error.name === 'AbortError') return 'Запрос прерван.'
  return 'Нет связи с сервером: проверьте, что agentbox запущен и доступен.'
}

/**
 * Сервер осознанно отказал в доступе — а не «мы до него не доехали».
 *
 * Разница принципиальная: 401/403 сервер вернул сам, значит токен
 * недействителен и сессии больше нет. Всё остальное — обрыв сети (fetch кинул),
 * таймаут (408, его ставит api.ts) или упавший сервер (5xx) — про токен не
 * говорит ничего. Смотрим на код ответа, а не на текст сообщения: текст
 * локализованный и меняется.
 */
export function isAuthFailure(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403)
}

/**
 * Пустить в оболочку по обрыву связи — это доверие на слово: токен могли
 * отозвать, пока телефон был офлайн. Поэтому как только сеть вернётся,
 * перепроверяем сессию тем же `bootstrap()`. Слушатель одноразовый, чтобы
 * повторные обрывы не навешивали их пачкой.
 */
let bootstrapRetryArmed = false

function retryBootstrapWhenOnline(get: () => AppState): void {
  if (bootstrapRetryArmed || typeof window === 'undefined') return
  bootstrapRetryArmed = true
  const onOnline = () => {
    window.removeEventListener('online', onOnline)
    bootstrapRetryArmed = false
    void get().bootstrap()
  }
  window.addEventListener('online', onOnline)
}

// ---------- стор ----------

export interface AppState {
  // — вход —
  booting: boolean
  authed: boolean
  host: string
  /** Профили панели: у каждого свой пароль и свои сессии. */
  profiles: ProfileInfo[]
  /** Имя профиля, под которым мы вошли (или которым входим). */
  profile: string | null
  /** Подпись текущего профиля для шапки. */
  profileLabel: string | null
  /** Сколько живых сессий держит сервер суммарно по всем профилям; 0 — без лимита. */
  maxSessions: number
  loginPending: boolean
  loginError: string | null
  /** Короткое сообщение об ошибке для статус-бара (создание сессии и т.п.). */
  notice: string | null

  // — оформление —
  theme: Theme
  /** Тему выбрал пользователь (§1.3, §13 abx-proto-v2). */
  themeTouched: boolean
  fontIndex: number

  // — раскладка —
  cols: Cols
  rows: Rows
  colFr: number
  rowFr: number

  // — окна и сессии —
  paneIds: string[]
  activeId: string | null
  dragFrom: number | null
  sessions: SessionInfo[]
  /** Идёт создание сессии: второй клик по «новому чату» плодит лишние чаты. */
  creating: boolean
  /** Сессии, которые прямо сейчас закрываются или удаляются. */
  busyIds: string[]

  // — связь —
  rtt: number | null
  linkState: LinkState

  // — боковое меню UI v3 (§1.2, §13) —
  /** Меню показано (десктоп). На время сессии страницы. */
  side: boolean
  /** Ширина: 56 (полоса) или 200–420. Хранится в localStorage['abx-side-w']. */
  sideW: number
  /** Шторка меню на телефоне (< 720 px). */
  mobSide: boolean
  /** Свёрнутые узлы меню: ключ `screen + ':' + name`. На время сессии. */
  sideFold: Record<string, boolean>
  /** Вкладки черновиков «новый раздел» (§10.1). */
  drafts: DraftTab[]
  /** Разделы, которые правят (§10.2): id раздела → id беседы правки. */
  secEdit: Partial<Record<Section, string>>
  /** Подсказка в поле ввода беседы (§10.1–10.2): id сессии → текст. Читает зона cli. */
  sessionHints: Record<string, string>
  /** §8: выбор из Библиотек для карточки полотна (зона canvas кладёт, pages читает). */
  libraryPick: LibraryPick | null
  setLibraryPick: (pick: LibraryPick | null) => void
  /** §9: Конструктор в режиме «расход» с этим агентом (pages кладёт, canvas читает). */
  flowFocus: FlowFocus | null
  setFlowFocus: (focus: FlowFocus | null) => void

  // — боковое меню старой оболочки —
  /** На десктопе это «меню показано» (оно вкладное), на телефоне — шторка. */
  menuOpen: boolean
  sidebarWidth: number
  chatsOpen: boolean
  baseOpen: boolean
  msgOpen: boolean

  // — разделы хаба —
  section: Section
  baseView: BaseView
  /** Открытый канал раздела «Мессенджеры». null — ещё не поднимали. */
  channelId: string | null
  /** Сервер отказал в канале Телеграма: он у профиля-владельца (403). */
  telegramDenied: string | null
  /** Сериф для чтения документа: переключатель «Aa» в шапке. */
  serif: boolean

  // — база знаний —
  tree: TreeNode[]
  /** Раскрытые каталоги дерева. */
  expanded: string[]
  /** Открытая заметка; null — не выбрана. */
  notePath: string | null
  /** Непрочитанных в ленте. */
  unread: number
  /** «Новых» в трекере сообщений (status=planned) — бейдж бровки, как unread у ленты. */
  trackerNew: number
  treeError: string | null
  /** Вид «документ» пишет другая ветка; сюда он кладёт «сохраняю…»,
   *  а бровка раздела рисует этим точку автосохранения. */
  noteSaving: boolean

  // — PWA —
  /** Сеть по мнению браузера: WS-состояние живёт отдельно, в linkState. */
  online: boolean
  /** Новая версия панели скачана и ждёт перезагрузки. */
  updateReady: boolean
  /** Браузер готов показать диалог установки. */
  canInstall: boolean
  /** Панель открыта как установленное приложение. */
  standalone: boolean
  /** iOS: диалога установки не будет, нужна подсказка про «На экран Домой». */
  ios: boolean

  bootstrap: () => Promise<void>
  loadProfiles: () => Promise<void>
  selectProfile: (name: string) => void
  login: (password: string, profile?: string | null) => Promise<void>
  logout: () => Promise<void>

  setTheme: (theme: Theme) => void
  toggleTheme: () => void
  cycleFont: () => void
  setFont: (index: number) => void

  setLayout: (cols: Cols, rows: Rows) => void
  setColFr: (fr: number) => void
  setRowFr: (fr: number) => void
  resetSplit: () => void

  refreshSessions: () => Promise<void>
  upsertSession: (info: SessionInfo) => void
  newSession: () => Promise<void>
  /** Открыть закреплённый канал «Телеграм»: переключает раздел на
   *  «Мессенджеры» и идемпотентно создаёт либо поднимает сессию канала. */
  openTelegram: () => Promise<void>
  /** Остановить процесс: разговор уезжает в историю и остаётся читаемым. */
  killSession: (sessionId: string) => Promise<void>
  /** Удалить сессию вместе с логом. Отменить нельзя. */
  purgeSession: (sessionId: string) => Promise<void>
  /** Стереть всю историю профиля. Живые сессии не трогает. */
  purgeArchive: () => Promise<void>

  openPane: (sessionId: string) => void
  closePane: (sessionId: string) => void
  focusPane: (sessionId: string) => void
  /** Клик по сессии в боковом меню: показать её в видимом слоте и сфокусировать. */
  focusSession: (sessionId: string) => void
  swapPanes: (from: number, to: number) => void
  setDragFrom: (index: number | null) => void
  /** Мобильный свайп: следующее/предыдущее открытое окно. */
  stepPane: (delta: number) => void

  setOnline: (online: boolean) => void
  setUpdateReady: (ready: boolean) => void
  setCanInstall: (can: boolean) => void
  /** Перезагрузить панель на новую версию. */
  installUpdate: () => void
  /** Показать системный диалог установки приложения. */
  install: () => Promise<void>

  setRtt: (rtt: number | null, state?: LinkState) => void
  setNotice: (text: string | null) => void
  toggleMenu: () => void
  closeMenu: () => void
  setSidebarWidth: (width: number) => void
  toggleChats: () => void
  toggleBase: () => void
  toggleMsg: () => void

  setSection: (section: Section) => void
  setBaseView: (view: BaseView) => void

  /** ☰ (§1.1): скрыто → показать; полоса → развернуть до 248; открыто → скрыть.
   *  На телефоне — открыть/закрыть шторку. */
  toggleSide: () => void
  /** Развернуть меню целиком (любой значок полосы 56). */
  expandSide: () => void
  /** Ширина при перетаскивании; persist — записать в localStorage. */
  setSideW: (width: number, persist?: boolean) => void
  /** Двойной клик по краю — вернуть 248. */
  resetSideW: () => void
  setMobSide: (open: boolean) => void
  /** Свернуть/развернуть узел меню по ключу `screen:name`. */
  toggleFold: (key: string) => void
  /** «+» в полосе: беседа «новый раздел» и вкладка черновика. */
  newSectionDraft: () => Promise<void>
  /** «✎» в полосе: беседа «правка: <раздел>», у раздела метка «черновик». */
  editSection: (section: Section, title: string) => Promise<void>
  /** Снять метку «черновик» / закрыть вкладку черновика (принять или отменить). */
  closeDraft: (section: Section) => void
  toggleSerif: () => void

  /** Перечитать дерево базы. Индекс на сервере обновляется по mtime, поэтому
   *  заметка, созданная агентом мимо API, появляется здесь сама. */
  refreshTree: () => Promise<void>
  /** Непрочитанные ленты — счётчик у секции «БАЗА ДАННЫХ» и на бровке. */
  refreshFeed: () => Promise<void>
  refreshTrackerNew: () => Promise<void>
  toggleFolder: (path: string) => void
  /** Открыть заметку: переключает раздел на базу и раскрывает путь к ней. */
  openNote: (path: string) => void
  /** Создать заметку в каталоге (пустой путь — корень базы). */
  createNote: (folder: string, title: string) => Promise<void>
  createFolder: (path: string) => Promise<void>
  setNoteSaving: (saving: boolean) => void
  /** Счётчик непрочитанного, когда точное значение уже пришло с сервера:
   *  экран ленты знает его из ответа и не должен ходить за ним второй раз. */
  setUnread: (unread: number) => void
  /** То же для бейджа «трекер» — TrackerView считает planned-строки сам. */
  setTrackerNew: (trackerNew: number) => void
}

export const useAppStore = create<AppState>()((set, get) => ({
  booting: true,
  authed: false,
  host: location.host,
  profiles: [],
  profile: savedProfile,
  profileLabel: null,
  // 0 — лимита по количеству нет (умолчание сервера с 25.10.2026)
  maxSessions: 0,
  loginPending: false,
  loginError: null,
  notice: null,

  theme: savedTheme,
  themeTouched: savedThemeTouched,
  fontIndex: savedFont >= 0 && savedFont < FONT_PAIRS.length ? savedFont : 0,

  cols: asCols(savedLayout.cols),
  rows: asRows(savedLayout.rows),
  colFr: asFr(savedLayout.colFr),
  rowFr: asFr(savedLayout.rowFr),

  paneIds: Array.isArray(savedPanes.ids) ? savedPanes.ids : [],
  activeId: savedPanes.active ?? null,
  dragFrom: null,
  sessions: [],
  creating: false,
  busyIds: [],

  rtt: null,
  linkState: 'stable',

  side: true,
  sideW: readSideW(),
  mobSide: false,
  sideFold: {},
  drafts: [],
  secEdit: {},
  sessionHints: {},
  libraryPick: null,
  setLibraryPick: (libraryPick) => set({ libraryPick }),
  flowFocus: null,
  setFlowFocus: (flowFocus) => set({ flowFocus }),

  // Вкладное меню по умолчанию открыто, шторка на телефоне — нет.
  menuOpen: isMobileLayout() ? false : savedMenu,
  sidebarWidth: asWidth(savedSide.width),
  chatsOpen: savedSide.chats !== false,
  baseOpen: savedSide.base !== false,
  msgOpen: savedSide.msg !== false,

  section: asSection(savedSection),
  baseView: asView(savedNotes.view),
  // id канала не переживает перезагрузку намеренно: после рестарта сервера
  // сессия та же, но найти её надёжнее по роли в списке, чем по старому id.
  channelId: null,
  telegramDenied: null,
  serif: savedSerif !== false,

  tree: [],
  expanded: Array.isArray(savedNotes.expanded) ? savedNotes.expanded : [],
  notePath: savedNotes.path ?? null,
  unread: 0,
  trackerNew: 0,
  treeError: null,
  noteSaving: false,

  online: navigator.onLine,
  updateReady: false,
  canInstall: false,
  standalone: isStandalone(),
  ios: isIos(),

  // Старт приложения: есть ли живой токен, кто мы, какие сессии на сервере.
  bootstrap: async () => {
    void get().loadProfiles()
    if (!getToken()) {
      set({ booting: false, authed: false })
      return
    }
    try {
      const me = await api.me()
      set({
        authed: true,
        host: me.host || location.host,
        profile: me.profile,
        profileLabel: me.label,
        maxSessions: me.maxSessions ?? 0,
        telegramDenied: me.telegramOwner === false ? TELEGRAM_DENIED : null,
        loginError: null,
        ...forgetPanesOfOtherProfile(me.profile),
      })
      await get().refreshSessions()
      adoptSessions(set, get)
      // База знаний профиля — сразу после входа: дерево нужно левому меню
      // в обоих разделах, а счётчик непрочитанных — секции «БАЗА ДАННЫХ».
      void get().refreshTree()
      void get().refreshFeed()
      void get().refreshTrackerNew()
    } catch (error) {
      if (isAuthFailure(error)) {
        // Токен недействителен — api.ts его уже стёр. Это единственный случай,
        // когда человека честно возвращают на форму входа.
        set({ authed: false, loginError: null })
      } else {
        // До сервера не доехали. Токен цел, значит сессия жива: для PWA
        // перезапуск без сети — обычный сценарий, и человек должен попасть в
        // оболочку с последним известным содержимым (M10.3, приёмка 7), а не
        // на форму входа. Дерево и лента идут через service worker
        // (network-first с откатом в кеш) и приедут из кеша; список сессий не
        // кешируется и просто останется пустым до возврата связи.
        set({ authed: true, loginError: null, notice: describeError(error) })
        void get().refreshSessions()
        void get().refreshTree()
        void get().refreshFeed()
        void get().refreshTrackerNew()
        retryBootstrapWhenOnline(get)
      }
    } finally {
      set({ booting: false })
    }
  },

  loadProfiles: async () => {
    try {
      const profiles = await api.profiles()
      set((s) => ({
        profiles,
        // выбранный профиль мог исчезнуть с сервера — тогда берём первый
        profile:
          s.profile && profiles.some((p) => p.name === s.profile)
            ? s.profile
            : profiles[0]?.name ?? null,
      }))
    } catch {
      // список профилей не критичен: без него форма входа спросит просто пароль
    }
  },

  selectProfile: (name) => set({ profile: name, loginError: null }),

  login: async (password: string, profile?: string | null) => {
    if (get().loginPending) return
    set({ loginPending: true, loginError: null })
    try {
      const target = profile === undefined ? get().profile : profile
      const auth = await api.login(password, target)
      setToken(auth.token)
      let host = get().host
      let slots = get().maxSessions
      let telegramDenied: string | null = null
      try {
        const me = await api.me()
        host = me.host || host
        slots = me.maxSessions ?? slots
        if (me.telegramOwner === false) telegramDenied = TELEGRAM_DENIED
      } catch {
        // /auth/me не обязателен для входа
      }
      set({
        authed: true,
        loginPending: false,
        loginError: null,
        host,
        maxSessions: slots,
        telegramDenied,
        profile: auth.profile,
        profileLabel: auth.label,
        ...forgetPanesOfOtherProfile(auth.profile),
      })
      await get().refreshSessions()
      adoptSessions(set, get)
      // База знаний профиля — сразу после входа: дерево нужно левому меню
      // в обоих разделах, а счётчик непрочитанных — секции «БАЗА ДАННЫХ».
      void get().refreshTree()
      void get().refreshFeed()
      void get().refreshTrackerNew()
    } catch (error) {
      set({ loginPending: false, loginError: describeError(error) })
    }
  },

  logout: async () => {
    try {
      await api.logout()
    } catch {
      // сервер мог уже забыть токен — выходим в любом случае
    }
    setToken(null)
    set({
      authed: false, sessions: [], paneIds: [], activeId: null, telegramDenied: null,
      menuOpen: false, dragFrom: null, rtt: null, linkState: 'stable', loginError: null,
      profileLabel: null,
      // база знаний у каждого профиля своя — чужое дерево на экране не оставляем
      tree: [], notePath: null, unread: 0, trackerNew: 0, treeError: null,
    })
    void get().loadProfiles()
  },

  setTheme: (theme) => {
    applyTheme(theme)
    set({ theme, themeTouched: true })
  },
  toggleTheme: () => get().setTheme(get().theme === 'dark' ? 'light' : 'dark'),

  setFont: (index) => {
    const safe = ((index % FONT_PAIRS.length) + FONT_PAIRS.length) % FONT_PAIRS.length
    applyFont(safe)
    set({ fontIndex: safe })
  },
  cycleFont: () => get().setFont(get().fontIndex + 1),

  // Смена раскладки: если слотов стало больше, чем открытых окон,
  // добираем свободные слоты живыми сессиями с сервера.
  setLayout: (cols, rows) =>
    set((s) => {
      const slots = cols * rows
      if (s.paneIds.length >= slots) return { cols, rows }
      const open = new Set(s.paneIds)
      const extra = s.sessions
        .filter((x) => !open.has(x.id) && x.state !== 'exited')
        .slice(0, slots - s.paneIds.length)
        .map((x) => x.id)
      if (extra.length === 0) return { cols, rows }
      const paneIds = [...s.paneIds, ...extra]
      return { cols, rows, paneIds, activeId: s.activeId ?? paneIds[0] ?? null }
    }),
  setColFr: (fr) => set({ colFr: fr }),
  setRowFr: (fr) => set({ rowFr: fr }),
  resetSplit: () => set({ colFr: 1, rowFr: 1 }),

  refreshSessions: async () => {
    try {
      const sessions = await api.listSessions()
      set((s) => {
        const alive = new Set(sessions.map((x) => x.id))
        // окна умерших сессий закрываем — сессии на сервере больше нет
        const paneIds = s.paneIds.filter((id) => alive.has(id))
        const activeId = s.activeId && paneIds.includes(s.activeId) ? s.activeId : paneIds[0] ?? null
        return { sessions, paneIds, activeId }
      })
    } catch {
      // список сессий — не критичная операция, шапка покажет состояние связи
    }
  },

  upsertSession: (info) =>
    set((s) => {
      const index = s.sessions.findIndex((x) => x.id === info.id)
      if (index === -1) return { sessions: [...s.sessions, info] }
      const sessions = [...s.sessions]
      sessions[index] = info
      return { sessions }
    }),

  newSession: async () => {
    // Двойное нажатие (и повторный заход по ярлыку) плодило пустые чаты.
    if (get().creating) return
    set({ creating: true })
    try {
      const session = await api.createSession({ type: 'agent' })
      set((s) => {
        const paneIds = [...s.paneIds, session.id]
        // подрастить раскладку, если новому окну не хватает слотов (как в прототипе)
        const fit: { cols: Cols; rows: Rows } =
          paneIds.length <= 1 ? { cols: 1, rows: 1 }
          : paneIds.length === 2 ? { cols: 2, rows: 1 }
          : { cols: 2, rows: 2 }
        const grow = s.cols * s.rows < paneIds.length ? fit : { cols: s.cols, rows: s.rows }
        return {
          sessions: s.sessions.some((x) => x.id === session.id) ? s.sessions : [...s.sessions, session],
          paneIds,
          activeId: session.id,
          // новое окно показывают в разделе бесед, даже если сейчас открыта база
          section: 'chats' as Section,
          ...grow,
        }
      })
    } catch (error) {
      set({ notice: describeError(error) })
    } finally {
      set({ creating: false })
    }
  },

  openTelegram: async () => {
    if (get().creating) return
    set({ creating: true, section: 'messengers' as Section })
    try {
      const session = await api.telegramSession()
      get().upsertSession(session)
      // В сетку бесед канал не кладём: у него свой раздел, а paneIds — это
      // окна раздела «Беседы». Иначе он занимал бы слот и его «×» выглядел
      // бы как закрытие канала, которого не бывает: канал закреплён.
      set({ channelId: session.id })
    } catch (error) {
      if (error instanceof ApiError && error.status === 403) set({ telegramDenied: detailOf(error.message) || TELEGRAM_DENIED })
      else set({ notice: describeError(error) })
    } finally {
      set({ creating: false })
    }
  },

  killSession: async (sessionId) => {
    await withBusy(set, get, sessionId, () => api.killSession(sessionId))
  },

  purgeSession: async (sessionId) => {
    await withBusy(set, get, sessionId, async () => {
      await api.purgeSession(sessionId)
      // окно удалённой сессии закрываем: смотреть в нём больше нечего
      set((s) => ({
        paneIds: s.paneIds.filter((id) => id !== sessionId),
        activeId: s.activeId === sessionId ? null : s.activeId,
        sessions: s.sessions.filter((x) => x.id !== sessionId),
      }))
    })
  },

  purgeArchive: async () => {
    try {
      const { removed } = await api.purgeArchive()
      await get().refreshSessions()
      set({
        notice:
          removed > 0 ? `История очищена: удалено чатов — ${removed}` : 'В истории пусто',
      })
    } catch (error) {
      set({ notice: describeError(error) })
    }
  },

  openPane: (sessionId) =>
    set((s) => (s.paneIds.includes(sessionId)
      ? { activeId: sessionId }
      : { paneIds: [...s.paneIds, sessionId], activeId: sessionId })),

  // Закрытие окна: сессия на сервере продолжает жить (FR-7).
  closePane: (sessionId) =>
    set((s) => {
      const paneIds = s.paneIds.filter((id) => id !== sessionId)
      const activeId = s.activeId === sessionId ? paneIds[0] ?? null : s.activeId
      return { paneIds, activeId, dragFrom: null }
    }),

  focusPane: (sessionId) => set({ activeId: sessionId }),

  // Клик по беседе всегда её открывает: раздел переключается на «Беседы», а
  // окно вне видимой раскладки переставляется в фокусный слот (SPEC, п. 3).
  // Меню закрываем только на телефоне: на десктопе оно вкладное, и прятать
  // его после каждого клика по списку — значит прятать сам список.
  focusSession: (sessionId) =>
    set((s) => {
      const shut = isMobileLayout() ? false : s.menuOpen
      const slots = s.cols * s.rows
      const paneIds = [...s.paneIds]
      const index = paneIds.indexOf(sessionId)
      if (index >= 0 && index < slots) {
        return { activeId: sessionId, menuOpen: shut, section: 'chats' as Section }
      }
      if (index >= 0) paneIds.splice(index, 1)
      // ставим в последний видимый слот, ничего не закрывая — вытесненные окна уезжают вправо
      paneIds.splice(Math.min(paneIds.length, slots - 1), 0, sessionId)
      return { paneIds, activeId: sessionId, menuOpen: shut, section: 'chats' as Section }
    }),

  swapPanes: (from, to) =>
    set((s) => {
      if (from === to || from < 0 || to < 0) return { dragFrom: null }
      if (from >= s.paneIds.length || to >= s.paneIds.length) return { dragFrom: null }
      const paneIds = [...s.paneIds]
      const carried = paneIds[from]
      paneIds[from] = paneIds[to]
      paneIds[to] = carried
      return { paneIds, dragFrom: null }
    }),

  setDragFrom: (index) => set({ dragFrom: index }),

  stepPane: (delta) =>
    set((s) => {
      if (s.paneIds.length === 0) return {}
      const current = s.activeId ? s.paneIds.indexOf(s.activeId) : 0
      const base = current === -1 ? 0 : current
      const next = (base + delta + s.paneIds.length) % s.paneIds.length
      return { activeId: s.paneIds[next] }
    }),

  setOnline: (online) => set({ online }),
  setUpdateReady: (updateReady) => set({ updateReady }),
  setCanInstall: (canInstall) => set({ canInstall }),
  installUpdate: () => applyUpdate(),
  install: async () => {
    const accepted = await promptInstall()
    set({ canInstall: false })
    if (accepted) set({ notice: 'Панель установлена: открывай её с домашнего экрана.' })
  },

  setRtt: (rtt, state) => set({ rtt, linkState: state ?? (rtt !== null && rtt > 250 ? 'slow' : 'stable') }),

  setNotice: (text) => set({ notice: text }),

  toggleMenu: () => set((s) => ({ menuOpen: !s.menuOpen })),
  closeMenu: () => set({ menuOpen: false }),

  setSidebarWidth: (width) => set({ sidebarWidth: asWidth(width) }),
  toggleChats: () => set((s) => ({ chatsOpen: !s.chatsOpen })),
  toggleBase: () => set((s) => ({ baseOpen: !s.baseOpen })),
  toggleMsg: () => set((s) => ({ msgOpen: !s.msgOpen })),

  // Раздел только переключается: окна бесед остаются смонтированными и держат
  // свои WebSocket'ы (критерий приёмки 8 контракта). Прячет их CSS, не React.
  setSection: (section) => {
    set({ section, mobSide: false })
    if (section === 'notes' || section === 'feed' || section === 'tracker') {
      void get().refreshTree()
      void get().refreshFeed()
      void get().refreshTrackerNew()
    }
    // Вход в «Мессенджеры» сам поднимает канал: сессия закреплённая, её не
    // создают руками, и пустой раздел с кнопкой был бы лишним шагом.
    if (section === 'messengers' && !get().telegramDenied && !get().sessions.some((x) => x.role === 'telegram')) {
      void get().openTelegram()
    }
  },

  // Виды старой «базы» разъехались по разделам UI v3: документ, граф и поиск —
  // «Заметки», лента — «Лента», трекер и канбан — «Трекер».
  setBaseView: (view) => {
    const section: Section =
      view === 'feed' ? 'feed' : view === 'tracker' || view === 'kanban' ? 'tracker' : 'notes'
    set({ section, baseView: view })
    if (view === 'feed') void get().refreshFeed()
    if (view === 'tracker') void get().refreshTrackerNew()
  },

  toggleSide: () => {
    if (isMobileLayout()) {
      set((s) => ({ mobSide: !s.mobSide }))
      return
    }
    const s = get()
    const rail = s.sideW < 120
    if (!s.side || rail) {
      set({ side: true, sideW: rail ? SIDE_DEFAULT : s.sideW })
      if (rail) writeSideW(SIDE_DEFAULT)
    } else {
      set({ side: false })
    }
  },

  expandSide: () => {
    set({ side: true, sideW: SIDE_DEFAULT })
    writeSideW(SIDE_DEFAULT)
  },

  setSideW: (width, persist = false) => {
    const sideW = clampSideW(width)
    set({ sideW })
    if (persist) writeSideW(sideW)
  },

  resetSideW: () => {
    set({ sideW: SIDE_DEFAULT })
    writeSideW(null)
  },

  setMobSide: (mobSide) => set({ mobSide }),

  toggleFold: (key) =>
    set((s) => ({ sideFold: { ...s.sideFold, [key]: !s.sideFold[key] } })),

  // §10: беседы-сборщики разделов живут в зоне pages (sections/custom/builder):
  // токен сборщика, правила UI-кита, серверные черновики.
  newSectionDraft: async () => {
    const { startNewSection } = await import('../sections/custom/builder')
    await startNewSection()
  },

  editSection: async (section, title) => {
    const { startEditSection } = await import('../sections/custom/builder')
    await startEditSection(section, title)
  },

  closeDraft: (section) =>
    set((s) => {
      const secEdit = { ...s.secEdit }
      delete secEdit[section]
      return {
        secEdit,
        drafts: s.drafts.filter((tab) => tab.id !== section),
        section: s.section === section ? ('chats' as Section) : s.section,
      }
    }),

  toggleSerif: () => {
    const serif = !get().serif
    applyRead(serif)
    set({ serif })
  },

  refreshTree: async () => {
    try {
      const tree = await notesApi.tree()
      set({ tree, treeError: null })
    } catch (error) {
      // Дерево не пришло — база могла быть ещё не поднята на сервере.
      // Роняем только секцию базы, беседы этого знать не должны.
      set({ treeError: describeError(error) })
    }
  },

  refreshFeed: async () => {
    try {
      const page = await notesApi.feed(1)
      set({ unread: page.unread })
    } catch {
      // счётчик непрочитанных — не повод показывать ошибку
    }
  },

  /** Бейдж «Трекер» в сайдбаре — количество необработанных (status=planned),
   *  видно с любого места панели, не только когда открыт сам трекер. */
  refreshTrackerNew: async () => {
    try {
      const rows = await trackerApi.messages('planned')
      set({ trackerNew: rows.length })
    } catch {
      // тот же принцип, что у refreshFeed: счётчик — не повод показывать ошибку
    }
  },

  toggleFolder: (path) =>
    set((s) => ({
      expanded: s.expanded.includes(path)
        ? s.expanded.filter((item) => item !== path)
        : [...s.expanded, path],
    })),

  openNote: (path) =>
    set((s) => {
      const expanded = [...s.expanded]
      for (const folder of ancestors(path)) {
        if (!expanded.includes(folder)) expanded.push(folder)
      }
      return {
        section: 'notes' as Section,
        baseView: 'document' as BaseView,
        notePath: path,
        expanded,
        // на телефоне меню — шторка поверх содержимого, её надо убрать
        menuOpen: isMobileLayout() ? false : s.menuOpen,
        mobSide: false,
      }
    }),

  createNote: async (folder, title) => {
    const name = title.trim()
    if (!name) return
    // Имя файла — это то, что человек написал: путь чинит сервер, а слэши
    // здесь означали бы «создай ещё и каталог», чего строка ввода не обещает.
    const file = `${name.replace(/[\\/]+/g, ' ').trim()}.md`
    const path = folder ? `${folder}/${file}` : file
    try {
      const note = await notesApi.create({ path, title: name })
      await get().refreshTree()
      get().openNote(note.path)
    } catch (error) {
      set({ notice: describeError(error) })
    }
  },

  createFolder: async (path) => {
    const clean = path.trim().replace(/^\/+|\/+$/g, '')
    if (!clean) return
    try {
      const folder = await notesApi.folder(clean)
      await get().refreshTree()
      set((s) => ({
        expanded: s.expanded.includes(folder.path) ? s.expanded : [...s.expanded, folder.path],
      }))
    } catch (error) {
      set({ notice: describeError(error) })
    }
  },

  setNoteSaving: (noteSaving) => set({ noteSaving }),

  setUnread: (unread) => set({ unread }),
  setTrackerNew: (trackerNew) => set({ trackerNew }),
}))

/** Пометить сессию занятой на время запроса и обновить список после него. */
async function withBusy(
  set: (partial: Partial<AppState> | ((s: AppState) => Partial<AppState>)) => void,
  get: () => AppState,
  sessionId: string,
  action: () => Promise<void>,
): Promise<void> {
  if (get().busyIds.includes(sessionId)) return
  set((s) => ({ busyIds: [...s.busyIds, sessionId] }))
  try {
    await action()
    await get().refreshSessions()
  } catch (error) {
    set({ notice: describeError(error) })
  } finally {
    set((s) => ({ busyIds: s.busyIds.filter((id) => id !== sessionId) }))
  }
}

/** Вход под другим профилем: окна прошлого профиля показывают чужие сессии.
 *  Сравниваем с профилем, прочитанным при загрузке модуля, а не с текущим
 *  значением в localStorage: его мог уже перезаписать loadProfiles(). */
function forgetPanesOfOtherProfile(profile: string): Partial<AppState> {
  if (!savedProfile || savedProfile === profile) return {}
  return { paneIds: [], activeId: null }
}

/** Если окон нет (первый вход или чистый профиль) — показываем первые сессии сервера. */
function adoptSessions(
  set: (partial: Partial<AppState>) => void,
  get: () => AppState,
): void {
  const s = get()
  if (s.paneIds.length > 0 || s.sessions.length === 0) return
  const slots = s.cols * s.rows
  // Сервер отдаёт сессии в порядке создания: первой оказывалась самая старая
  // (часто завершённая), и Беседы после входа открывали её, а не свежую живую.
  // Канал Телеграма живёт в Мессенджерах — в окна Бесед его не берём.
  const ranked = s.sessions
    .filter((x) => x.role !== 'telegram')
    .sort((a, b) => Number(a.state === 'exited') - Number(b.state === 'exited') || b.lastActivity - a.lastActivity)
  const paneIds = ranked.slice(0, slots).map((x) => x.id)
  set({ paneIds, activeId: paneIds[0] ?? null })
}

let lastProto = ''
function saveProto(value: Proto): void {
  const raw = JSON.stringify(value)
  if (raw === lastProto) return
  lastProto = raw
  try {
    localStorage.setItem(PROTO_KEY, raw)
  } catch {
    // не критично
  }
}

// Первичное применение сохранённого оформления (критерий приёмки 7).
applyTheme(useAppStore.getState().theme)
applyFont(useAppStore.getState().fontIndex)
applyRead(useAppStore.getState().serif)

// Сохранение того, что должно пережить перезагрузку (критерии приёмки 3 и 7).
useAppStore.subscribe((s) => {
  saveIfChanged('font', s.fontIndex)
  saveIfChanged('layout', { cols: s.cols, rows: s.rows, colFr: s.colFr, rowFr: s.rowFr })
  saveIfChanged('panes', { ids: s.paneIds, active: s.activeId })
  // §13: abx-proto-v2 = {screen, theme, themeTouched}. Черновики не переживают
  // перезагрузку — в screen пишем только постоянные разделы.
  saveProto({
    screen: s.section.startsWith('draft:') ? 'chats' : s.section,
    theme: s.theme,
    themeTouched: s.themeTouched,
  })
  saveIfChanged('serif', s.serif)
  saveIfChanged('sidebar', {
    width: s.sidebarWidth,
    chats: s.chatsOpen,
    base: s.baseOpen,
    msg: s.msgOpen,
  })
  saveIfChanged('notes', { view: s.baseView, path: s.notePath, expanded: s.expanded })
  // Открытость меню помним только на десктопе: на телефоне это шторка,
  // и запомненная «открытой» она встречала бы человека поверх разговора.
  if (!isMobileLayout()) saveIfChanged('menu', s.menuOpen)
  if (s.profile) saveIfChanged('profile', s.profile)
})
